import { FC, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useNavigate, useParams } from 'react-router-dom';
import Balancer from 'react-wrap-balancer';

import { Button } from '../../components/form/Button';
import { DecorativeCard } from '../../layouts/DecorativeCard';
import { useSignUp } from '../../services/authentication/hooks/auth';
import { PageTitle } from '../../utils/Title';

import { useAnonymousInvite } from '@/services/api/hooks/invites';
import i18n from './invite.translation';
import { useAppInsights } from '@/services/insights/useAppInsights';

interface RedeemInviteParams extends Record<string, string> {
  inviteId: string;
}

export const InviteCard: FC = () => {
  const navigate = useNavigate();

  const { inviteId } = useParams<RedeemInviteParams>();
  const trackEvent = useAppInsights(state => state.trackEvent);

  const signUp = useSignUp();

  if (!inviteId) throw new Error('No invite id provided');
  const [invite] = useAnonymousInvite(inviteId);

  useEffect(() => trackEvent('INVITE:OPEN', { inviteId }), [inviteId, trackEvent]);

  return (
    <>
      <PageTitle>Redeem invite</PageTitle>
      <h1 className="text-center text-4xl font-bold">
        <Balancer>{i18n.title} </Balancer>
      </h1>
      {invite.body.redeemed ? (
        <div className="space-x-1">
          <span className="font-medium">{i18n.alreadyRedeemedPt1}</span>
          <span onClick={() => navigate('/auth/signin')} className="font-medium text-blue-500 underline cursor-pointer">
            {i18n.alreadyRedeemedPt2}
          </span>
          <span className="font-medium">{i18n.alreadyRedeemedPt3}</span>
        </div>
      ) : (
        <Button onClick={signUp} className="w-full">
          Create an account and get started
        </Button>
      )}
    </>
  );
};

export const ErrorCard: FC = () => {
  return (
    <div>
      <h1>Something went wrong</h1>
      <p>Try again later</p>
    </div>
  );
};

export const RedeemInvite: FC = () => {
  return (
    <DecorativeCard className="flex w-full max-w-2xl flex-col items-center gap-8 p-16">
      <ErrorBoundary fallback={<ErrorCard />}>
        <InviteCard />
      </ErrorBoundary>
    </DecorativeCard>
  );
};
